import { useQuery } from "@apollo/client";
import { gql } from "__generated__";
import Loading from "_components/Loading";
import React from "react";
import { useHistory, useParams } from "react-router";
import { employmentTypeToText } from "..";
import RequestFailed from "_components/RequestFailed";
import { format } from "date-fns";
import {
  Button,
  Icon,
  Tag,
  TagLabel,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
  HStack,
  Link,
} from "@chakra-ui/react";
import {
  FaArrowRight,
  FaRegMoneyBillAlt,
  FaUsers,
  FaFacebookF,
  FaTwitter,
  FaArrowLeft,
} from "react-icons/fa";
import { GrLocation, GrUserWorker } from "react-icons/gr";
import {
  formatAsCurrency,
  formatContractJobAdDuration,
} from "helpers/utils/funtions";
import { RiBuildingLine } from "react-icons/ri";
import { MdOutlineBusinessCenter } from "react-icons/md";
import { Tags, Share } from "lucide-react";
import { IoCheckmarkCircle } from "react-icons/io5";
import { logEvent } from "helpers/analytics";
import { RxCounterClockwiseClock } from "react-icons/rx";
import { useLocation } from "react-router-dom";
import Footer from "_components/Footer";
import { useEmbed } from "../../../hooks/useEmbed";
import { useEmbedResize } from "../../../hooks/useEmbedResize";

const GET_JOB_AD = gql(`
	query GetJobAd($id: String!) {
		getEmployerJobAd(id: $id) {
			id
			title
			description
			companyName
			salaryDetails {
        starting
        cap
        currencyCode
      }
			createdAt
			employmentType
      duration {
        value
        type
      }
      principal {
        name
      }
      visibility
      organization {
        name
      }
      tags {
        id
        name
      }
			location {
			  area
				state
				country
			}
			categories {
				name
			}
      hasApplied
      canApply
      applicationDeadline
		}
	}
`);
export default function SingleJobView() {
  const { id } =
    useParams<{
      id: string;
    }>();

  const history = useHistory();
  const { isEmbedded } = useEmbed();
  useEmbedResize();

  const { pathname } = useLocation();
  const [showShareModal, setShowShareModal] = React.useState(false);

  const {
    loading: jobLoading,
    error: jobError,
    data: jobData,
    refetch: refetchJobData,
  } = useQuery(GET_JOB_AD, {
    variables: {
      id,
    },
  });

  const jobAd = jobData?.getEmployerJobAd;

  const handleJobShare = () => {
    if (!jobAd) return;

    const url = `${window.location.origin}${pathname}`;

    logEvent("job_share_clicked", {
      job_id: jobAd.id,
      job_title: jobAd.title,
    });

    if (navigator.share) {
      navigator
        .share({
          title: `${jobAd.title} | LaborHack Jobs`,
          text: `Check out this job opportunity: ${jobAd.title}`,
          url,
        })
        .then(() => {
          logEvent("job_shared", {
            job_id: jobAd.id,
            job_title: jobAd.title,
          });
        })
        .catch((error) => {
          if (error.name === "AbortError") {
            return;
          }

          logEvent("job_share_failed", {
            job_id: jobAd.id,
            job_title: jobAd.title,
            error: error.message,
          });

          setShowShareModal(true);
        });
    } else {
      setShowShareModal(true);
    }
  };

  if (jobError) {
    return <RequestFailed text={jobError.message} />;
  }

  return (
    <div>
      <main>
        <Modal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          isCentered
          closeOnOverlayClick
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Share Job</ModalHeader>
            <VStack pb={8} px={6} alignItems="start">
              <HStack>
                <Link
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                    `Check out this job opportunity: ${
                      jobAd?.title || "Job"
                    } on LaborHack`
                  )}&url=${encodeURIComponent(
                    `${window.location.origin}${pathname}`
                  )}`}
                  isExternal
                >
                  <Button
                    rightIcon={<FaTwitter />}
                    colorScheme="twitter"
                    variant="solid"
                    onClick={() =>
                      logEvent("job_twitter_share_clicked", {
                        job_id: jobAd?.id,
                        job_title: jobAd?.title,
                      })
                    }
                  >
                    Tweet
                  </Button>
                </Link>
                <Link
                  href={
                    "https://www.facebook.com/sharer/sharer.php?u=" +
                    encodeURIComponent(`${window.location.origin}${pathname}`)
                  }
                  isExternal
                >
                  <Button
                    rightIcon={<FaFacebookF />}
                    colorScheme="facebook"
                    variant="solid"
                    onClick={() =>
                      logEvent("job_facebook_share_clicked", {
                        job_id: jobAd?.id,
                        job_title: jobAd?.title,
                      })
                    }
                  >
                    Post to Facebook
                  </Button>
                </Link>
              </HStack>
            </VStack>
          </ModalContent>
        </Modal>
        {jobLoading ? (
          <div className="flex justify-center items-center h-screen">
            <Loading />
          </div>
        ) : jobAd ? (
          <section className="px-3 lg:px-[75px] w-full">
            <div className="w-full border-[#ACACAC] pt-8 lg:pt-16">
              <div className="w-full">
                <div className="mb-6 lg:mb-8">
                  <Button
                    leftIcon={<Icon as={FaArrowLeft} w={4} h={4} />}
                    variant="link"
                    colorScheme="prussianBlue"
                    fontSize={{
                      base: "14px",
                      lg: "16px",
                    }}
                    onClick={() =>
                      history.push(`/jobs${isEmbedded ? "?embed=true" : ""}`)
                    }
                  >
                    Back to Jobs
                  </Button>
                </div>
                <div className="border-[#ACACAC] border-b-[1px] pb-6 lg:pb-8 flex flex-col lg:flex-row gap-4 lg:justify-between lg:items-end">
                  <div className="w-full flex flex-col gap-4 lg:gap-6">
                    <div className="w-[85%] lg:w-[55%]">
                      <h3 className="text-[#0E212D] text-[18px] lg:text-[23px] font-medium">
                        {jobAd.title}
                      </h3>
                    </div>
                    <div className="flex flex-col gap-2">
                      <p className="text-[#6E6E6E] text-[14px] lg:text-[18px] font-normal">
                        Date posted:{" "}
                        <span className="text-[#000000]">
                          {format(new Date(jobAd.createdAt), "PPP")}
                        </span>
                      </p>
                      {jobAd.applicationDeadline && (
                        <div className="flex items-center gap-2">
                          <p className="text-[#8d524a] text-[14px] lg:text-[18px]">
                            Application deadline:{" "}
                            <span className="text-[#883227]">
                              {format(
                                new Date(jobAd.applicationDeadline),
                                "PPP"
                              )}
                            </span>
                          </p>
                        </div>
                      )}
                      {jobAd.visibility === "PRIVATE" && (
                        <div className="flex items-center gap-2">
                          <p className="text-[#6E6E6E] text-[14px] lg:text-[18px] font-normal">
                            Exclusive to{" "}
                            <span className="text-[#000000]">
                              {jobAd.organization.name}
                            </span>{" "}
                            members
                          </p>
                          <FaUsers className="text-[#6E6E6E]" size={16} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col lg:flex-row gap-3">
                    <Button
                      leftIcon={<Icon as={Share} w={4} h={4} />}
                      colorScheme="prussianBlue"
                      variant="outline"
                      h={{
                        base: "40px",
                        lg: "57px",
                      }}
                      fontSize={{
                        base: "16px",
                        lg: "20px",
                      }}
                      onClick={handleJobShare}
                    >
                      Share
                    </Button>
                    <Button
                      bgColor={
                        jobAd.hasApplied && !jobAd.canApply
                          ? "#FBE2DE"
                          : "#ED6E5C"
                      }
                      color={
                        jobAd.hasApplied && !jobAd.canApply
                          ? "#F08677"
                          : "#FEFEFE"
                      }
                      h={{
                        base: "40px",
                        lg: "57px",
                      }}
                      fontSize={{
                        base: "16px",
                        lg: "20px",
                      }}
                      rightIcon={
                        <Icon
                          as={
                            jobAd.hasApplied && !jobAd.canApply
                              ? IoCheckmarkCircle
                              : FaArrowRight
                          }
                          fill={
                            jobAd.hasApplied && !jobAd.canApply
                              ? "#F08677"
                              : "#FEFEFE"
                          }
                          w={{
                            base: 4,
                            lg: 5,
                          }}
                          h={{
                            base: 4,
                            lg: 5,
                          }}
                          ml={0}
                          fontWeight={"500"}
                        />
                      }
                      isDisabled={jobAd.hasApplied && !jobAd.canApply}
                      onClick={() => {
                        logEvent("job_application_started_from_job_board", {
                          job_id: jobAd.id,
                          job_title: jobAd.title,
                        });

                        history.push(
                          `/jobs/${jobAd.id}/apply${isEmbedded ? "?embed=true" : ""}`
                        );
                      }}
                    >
                      {jobAd.hasApplied && !jobAd.canApply
                        ? "Applied"
                        : "Apply"}
                    </Button>
                  </div>
                </div>
                <div className="border-[#ACACAC] border-b-[1px] py-6 lg:py-8">
                  <div
                    className="markdown"
                    dangerouslySetInnerHTML={{
                      __html: jobAd.description,
                    }}
                  />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-[3fr,5fr] gap-6 lg:gap-8 py-6 lg:py-8">
                  <div className="flex items-center gap-4">
                    <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                      <Icon
                        as={GrLocation}
                        w={{
                          base: "20px",
                          lg: "20px",
                        }}
                        h={{
                          base: "20px",
                          lg: "20px",
                        }}
                        color={"#212121"}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                        Location
                      </p>
                      <p className="text-[#212121] text-[16px] lg:text-[20px]">
                        {`${jobAd.location.area}, ${jobAd.location.state}, ${jobAd.location.country}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                      <Icon
                        as={MdOutlineBusinessCenter}
                        w={{
                          base: "20px",
                          lg: "20px",
                        }}
                        h={{
                          base: "20px",
                          lg: "20px",
                        }}
                        color={"#212121"}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                        Employment type
                      </p>
                      <p className="text-[#212121] text-[16px] lg:text-[20px]">
                        {`${employmentTypeToText(jobAd.employmentType)}`}
                      </p>
                    </div>
                  </div>

                  {jobAd.duration && (
                    <div className="flex items-center gap-4">
                      <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                        <Icon
                          as={RxCounterClockwiseClock}
                          w={{
                            base: "20px",
                            lg: "20px",
                          }}
                          h={{
                            base: "20px",
                            lg: "20px",
                          }}
                          color={"#212121"}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                          Duration
                        </p>
                        <p className="text-[#212121] text-[16px] lg:text-[20px]">
                          {formatContractJobAdDuration(jobAd.duration)}
                        </p>
                      </div>
                    </div>
                  )}

                  {jobAd.salaryDetails && (
                    <div className="flex items-center gap-4">
                      <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                        <Icon
                          as={FaRegMoneyBillAlt}
                          w={{
                            base: "20px",
                            lg: "20px",
                          }}
                          h={{
                            base: "20px",
                            lg: "20px",
                          }}
                          color={"#212121"}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                          {`Salary ${jobAd.salaryDetails.cap ? "range" : ""}`}
                        </p>
                        <p className="text-[#212121] text-[16px] lg:text-[20px]">
                          {formatAsCurrency(
                            jobAd.salaryDetails.starting,
                            jobAd.salaryDetails.currencyCode
                          )}{" "}
                          {jobAd.salaryDetails.cap
                            ? `- ${formatAsCurrency(
                                jobAd.salaryDetails.cap,
                                jobAd.salaryDetails.currencyCode
                              )}`
                            : ""}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-4">
                    <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                      <Icon
                        as={RiBuildingLine}
                        w={{
                          base: "20px",
                          lg: "20px",
                        }}
                        h={{
                          base: "20px",
                          lg: "20px",
                        }}
                        color={"#212121"}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                        Employer
                      </p>
                      <p className="text-[#212121] text-[16px] lg:text-[20px]">
                        {jobAd.companyName}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                      <Icon
                        as={Tags}
                        w={{
                          base: "20px",
                          lg: "20px",
                        }}
                        h={{
                          base: "20px",
                          lg: "20px",
                        }}
                        color={"#212121"}
                      />
                    </div>
                    {jobAd.tags.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                          Tags
                        </p>

                        <div className="flex flex-wrap gap-2">
                          {jobAd.tags.map((tag) => (
                            <Tag
                              size={"lg"}
                              key={tag.id}
                              borderRadius="full"
                              colorScheme={"lavender"}
                            >
                              <TagLabel>{tag.name}</TagLabel>
                            </Tag>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
                          {jobAd.categories.length > 1
                            ? "Categories"
                            : "Category"}
                        </p>
                        <p className="text-[#212121] text-[16px] lg:text-[20px]">
                          {jobAd.categories
                            .map((category) => category.name)
                            .join(", ")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        ) : (
          !jobAd &&
          !jobLoading && (
            <div className="flex items-center justify-center h-full p-4 lg:p-8 border-[1px] border-[#ACACAC]">
              <p className="text-[#0E212D] text-lg lg:text-xl">Job not found</p>
            </div>
          )
        )}
      </main>
      <Footer />
    </div>
  );
}
